import { create } from 'zustand';
import type { ChatResourceEvent } from '../../hooks/use-chat-ws';

// Lets a page subscribe to the widget's resource events without changing session
// semantics (unlike investigation scope). The agent builder uses this to refetch
// its graph when Percy lands an agent edit mid-turn. Object-valued so a consumer
// never treats the callback as a functional updater.
export type ChatResourceListener = {
  onResourceEvent: (event: ChatResourceEvent) => void;
  /**
   * Called on every websocket `ready` handshake — initial connection and each
   * reconnect. Resource events are live-only, so one emitted while the socket
   * was down is gone for good; refetch here to pick up what landed in the gap.
   */
  onReady?: () => void;
  /**
   * Lets a "Percy proposed N changes" card open the Changes panel in place when
   * the builder for that agent is already mounted. Returns false when the open
   * builder is for a different agent, so the card falls back to navigation.
   */
  openSuggestions?: (agentId: string) => boolean;
  /** Fires when Percy's turn ends, so a page can hand the chat back after driving the screen. */
  onTurnComplete?: () => void;
};

// One global widget, so the page→widget listener handoff is app-global state in a module store: a
// page publishes via setListener (clearing on unmount), the widget reads it.
type ChatResourceListenerStore = {
  listener: ChatResourceListener | null;
  setListener: (listener: ChatResourceListener | null) => void;
};

const useStore = create<ChatResourceListenerStore>(set => ({
  listener: null,
  setListener: listener => set({ listener }),
}));

export const useChatResourceListener = (): ChatResourceListener | null => useStore(state => state.listener);

/** For a page to publish / clear its listener. */
export const useSetChatResourceListener = (): ((listener: ChatResourceListener | null) => void) =>
  useStore(state => state.setListener);
